import { useState, ReactNode, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  trigger: ReactNode;
}

const Schema = z.object({
  name: z.string().trim().min(1).max(120),
  company: z.string().trim().min(1).max(160),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().max(40).optional(),
  message: z.string().trim().max(2000).optional(),
  role: z.enum(["broker", "supplier", "other"]),
});

const DemoDialog = ({ trigger }: Props) => {
  const { t, lang } = useLang();
  const location = useLocation();
  const defaultRole: "broker" | "supplier" | "other" = useMemo(() => {
    const p = location.pathname;
    if (p.includes("makelaar") || p.includes("broker")) return "broker";
    if (p.includes("leverancier") || p.includes("supplier")) return "supplier";
    return "other";
  }, [location.pathname]);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "", role: defaultRole });

  const isNL = lang === "nl";
  const roleLabel = isNL ? "Ik ben" : "I am";
  const roleOptions: { value: "broker" | "supplier" | "other"; label: string }[] = [
    { value: "broker", label: isNL ? "Makelaar" : "Broker" },
    { value: "supplier", label: isNL ? "Leverancier" : "Supplier" },
    { value: "other", label: isNL ? "Anders" : "Other" },
  ];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = Schema.safeParse(form);
    if (!parsed.success) {
      toast.error(t.demo.error);
      return;
    }
    setLoading(true);
    try {
      const { track } = await import("@/lib/analytics");
      track("demo_open", "submit");
      const { error } = await supabase.functions.invoke("send-contact", {
        body: { ...parsed.data, type: "demo", lang, source_path: location.pathname },
      });
      if (error) throw error;
      toast.success(t.demo.success);
      setOpen(false);
      setForm({ name: "", company: "", email: "", phone: "", message: "", role: defaultRole });
    } catch (err) {
      console.error(err);
      toast.error(t.demo.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setForm((f) => ({ ...f, role: defaultRole })); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{t.demo.title}</DialogTitle>
          <DialogDescription>{t.demo.desc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label>{roleLabel}</Label>
            <RadioGroup
              value={form.role}
              onValueChange={(v) => setForm({ ...form, role: v as "broker" | "supplier" | "other" })}
              className="flex flex-wrap gap-3"
            >
              {roleOptions.map((r) => (
                <label
                  key={r.value}
                  htmlFor={`role-${r.value}`}
                  className={`flex items-center gap-2 rounded-full border px-4 py-2 cursor-pointer text-sm font-medium transition-colors ${form.role === r.value ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border"}`}
                >
                  <RadioGroupItem value={r.value} id={`role-${r.value}`} className="sr-only" />
                  {r.label}
                </label>
              ))}
            </RadioGroup>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="d-name">{t.demo.name}</Label>
              <Input id="d-name" required maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.demo.namePh} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="d-company">{t.demo.company}</Label>
              <Input id="d-company" required maxLength={160} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={t.demo.companyPh} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="d-email">{t.demo.email}</Label>
              <Input id="d-email" type="email" required maxLength={200} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.demo.emailPh} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="d-phone">{t.demo.phone}</Label>
              <Input id="d-phone" maxLength={40} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t.demo.phonePh} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="d-msg">{t.demo.message}</Label>
            <Textarea id="d-msg" rows={4} maxLength={2000} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t.demo.messagePh} />
          </div>
          <Button type="submit" size="lg" disabled={loading} className="w-full rounded-full h-12 text-base">
            {loading ? t.demo.sending : t.demo.submit}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DemoDialog;
